import { Component, Input } from '@angular/core';
import { Submission, Review } from 'src/app/model/paper';

/**
 * `<app-submission-export-view>` — wrapper READ-ONLY que compõe todos os
 * sub-componentes de apresentação (`<app-submission-header>`,
 * `<app-submission-authors-display>`, `<app-submission-abstract-display>`,
 * `<app-submission-topics-display>`, `<app-submission-session-display>`,
 * `<app-submission-extra-fields-display>`) + reutiliza os componentes
 * READ-ONLY existentes (`<app-paper-publication-info>`,
 * `<app-paper-review-list>`, `<app-rebuttal>`, `<app-discussion-messages>`).
 *
 * Passo 7 da migração. Zero HTTP, zero forms, zero edit-mode. Toda a lógica
 * de visibilidade/permissão é feita PELO CALLER (paper-info) e passada via
 * inputs — este componente só renderiza. Objetivo: no passo 8, o
 * `downloadSubmissionHtmlPage()` instancia este wrapper dinamicamente
 * (`ViewContainerRef.createComponent`), captura o outerHTML e monta o doc
 * final — SEM depender do clone do DOM vivo do paper-info.
 */
@Component({
  selector: 'app-submission-export-view',
  templateUrl: './submission-export-view.component.html',
  styleUrls: ['./submission-export-view.component.scss'],
})
export class SubmissionExportViewComponent {
  // NOTA: NÃO medimos `#abstractLead` aqui — o wrapper é instanciado OFFSCREEN
  // no export (container em `position:fixed; left:-100000px`), contexto em que
  // a fonte pode não estar estabilizada quando medimos → sai valor 5-10% maior
  // que o real (ex.: 62.96px em vez de 58px). Esse valor viraria inline style
  // no HTML exportado, ficando ERRADO quando aberto (que renderiza 58px).
  //
  // Solução: no HTML exportado, um `<script>` inline mede `#abstractLead` no
  // DOMContentLoaded e seta `--abstract-lead-w` no `.abstract-block` — mesma
  // técnica do paper-info vivo, mas rodando no contexto de renderização final.
  // O script é injetado por `buildSubmissionHtmlDocumentFromWrapper`.

  /** Submissão hidratada (com `event`, `track`, `authors`, `topics`, `session`,
   *  `formAnswer`/`getSubmissionFormAnswers()`, etc.). Passada crua; o
   *  wrapper delega o processamento aos sub-componentes. */
  @Input() submission!: Submission;
  /** Revisões (com `form`, `answers`) — passadas pro `<app-paper-review-list>`. */
  @Input() reviews: Review[] = [];
  /** Papel do visualizador — decide links/labels específicos (chair vira nomes
   *  de autor em `<a>` accent; autor/revisor mostram trilha como subtítulo, etc.). */
  @Input() viewerRole: 'author' | 'reviewer' | 'chair' | 'session-chair' = 'author';
  /** Config de revisões (rebuttal on/off, discussion on/off, prazos) — passada
   *  pros componentes de rebuttal/discussion. */
  @Input() reviewConfig: any = null;
  /** Timezone do evento pra formatar datas na sessão + discussão + rebuttal. */
  @Input() eventTimeZone: string | null = null;
  /** Linha "Trilha: X - Grupo: Y - TPC Group: Z" pro export chair — repassada
   *  ao `<app-submission-header>` que renderiza como subtítulo centralizado
   *  no lugar do `.event-track` (substitui a meta-card strippada no export). */
  @Input() chairMetaLabel: string = '';
  /** true na visão CHAIR — passa pro header (troca `<a>` do nome do evento
   *  por `<span>` + sinaliza pra outros componentes se preciso). */
  @Input() isChairInterface = false;
  /** true = exibe o carimbo de status na folha (visão NÃO-chair com submissão). */
  @Input() showStamp = false;
  /** Rótulo pronto do acrônimo do evento ("SBRC 2025") — usado como fallback
   *  quando o nome completo não cabe na linha do header. */
  @Input() eventAcronymLabel = '';
  /** true = usa `eventAcronymLabel` no lugar do nome completo (paper-info
   *  decide via measurement; export sempre passa false — nome completo). */
  @Input() useEventAcronym = false;
  /** viewMode passado ao `<app-paper-review-list>` — espelha o que paper-info
   *  vivo estava mostrando ('full' individual; 'show-all-submissions' massa). */
  @Input() reviewsViewMode: 'full' | 'show-all-submissions' = 'full';
  /** Layout dos cards de revisão — repassado ao `<app-paper-review-list>` pra
   *  espelhar a preferência atual do usuário (stacked / mixed / side-by-side).
   *  Default 'stacked' bate com o default do próprio review-list. */
  @Input() reviewsLayout: 'stacked' | 'mixed' | 'side-by-side' = 'stacked';

  // Getters de conveniência pros sub-componentes.
  public get showTrack(): boolean {
    return this.viewerRole !== 'chair' && !!(this.submission?.track as any)?.name;
  }
  public get extraFieldAnswers(): any[] {
    return (this.submission?.getSubmissionFormAnswers() || [])
      .slice()
      .sort((a: any, b: any) => a.order - b.order)
      .map((a: any) => ({
        ...a,
        answers: (a.answers || []).filter((v: any) => v != null && String(v).trim() !== ''),
      }));
  }
  public get hasExtraFields(): boolean {
    return this.extraFieldAnswers.length > 0;
  }
}
